import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiEdit2, FiTrash2, FiUserPlus, FiAlertTriangle } from "react-icons/fi";
import { FaSync, FaUserShield } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../core/common/header/PageHeader";

type AnyObj = Record<string, any>;

interface User {
  studentId: number;
  firstName: string;
  lastName: string;
  email: string;
  whatsappNumber?: number | string;
  role: string;
  activeStudent?: boolean;
}

const normalizeRole = (r: any): string => {
  if (r == null) return "";
  const t = String(r).trim().toUpperCase();
  return t.startsWith("ROLE_") ? t.slice(5) : t;
};

const getMentorIdFromBatch = (b: AnyObj): number | null => {
  if (typeof b.mentorId === "number") return b.mentorId;
  if (b.mentor && typeof b.mentor.studentId === "number")
    return b.mentor.studentId;
  if (typeof b.mentor_id === "number") return b.mentor_id;
  return null;
};

const isBatchActive = (b: AnyObj): boolean => {
  const v =
    b.isActive ??
    b.is_active ??
    (b.batch && (b.batch.isActive ?? b.batch.active ?? b.batch.is_active));
  return typeof v === "boolean" ? v : true;
};

const getBatchName = (b: AnyObj): string | null => {
  return b.name ?? b.batchName ?? b.batch?.name ?? null;
};

const ManageAdminsMentors: React.FC = () => {
  const { logout } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [batches, setBatches] = useState<AnyObj[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    userId: number | null;
    userName: string;
    userRole: string;
  }>({
    isOpen: false,
    userId: null,
    userName: "",
    userRole: "",
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resUsers, resBatches] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/students`, {
          credentials: "include",
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/batches`, {
          credentials: "include",
        }),
      ]);

      if (!resUsers.ok) {
        if (resUsers.status === 401) logout();
        throw new Error(`Failed to load users (${resUsers.status})`);
      }
      if (!resBatches.ok) {
        if (resBatches.status === 401) logout();
        console.warn("Failed to load batches:", resBatches.status);
      }

      const rawUsers = await resUsers.json();
      const rawBatches = resBatches.ok ? await resBatches.json() : [];

      const normalized: User[] = (Array.isArray(rawUsers) ? rawUsers : []).map(
        (u: AnyObj) => ({
          studentId: u.studentId ?? u.id,
          firstName: u.firstName,
          lastName: u.lastName,
          email: u.email,
          whatsappNumber: u.whatsappNumber ?? u.whatsapp_number,
          role: normalizeRole(u.role),
          activeStudent:
            u.activeStudent ?? u.isActiveStudent ?? u.active ?? false,
        })
      );

      setUsers(normalized);
      setBatches(
        Array.isArray(rawBatches) ? rawBatches : rawBatches.batches || []
      );
      setErr(null);
    } catch (e: any) {
      setErr(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout]);

  const adminsMentors = useMemo(() => {
    const list = users.filter((u) => {
      const r = normalizeRole(u.role);
      return r === "ADMIN" || r === "MENTOR";
    });
    list.sort((a, b) => (b.studentId ?? 0) - (a.studentId ?? 0));
    return list;
  }, [users]);

  const batchNamesByMentorId = useMemo(() => {
    const map: Record<number, Set<string>> = {};
    for (const b of batches) {
      const name = getBatchName(b);
      const mentorId = getMentorIdFromBatch(b);
      if (!name || mentorId == null) continue;
      if (!isBatchActive(b)) continue;
      (map[mentorId] ||= new Set()).add(name);
    }
    const out: Record<number, string[]> = {};
    Object.keys(map).forEach(
      (k) => (out[+k] = Array.from(map[+k]).sort((a, b) => a.localeCompare(b)))
    );
    return out;
  }, [batches]);

  const handleDelete = async (id: number, role: string) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/mentors/${id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Delete failed (${res.status})`);
      }

      setDeleteModal({
        isOpen: false,
        userId: null,
        userName: "",
        userRole: "",
      });
      fetchData();
    } catch (e: any) {
      alert("Delete error: " + e.message);
      setDeleteModal({
        isOpen: false,
        userId: null,
        userName: "",
        userRole: "",
      });
    }
  };

  const openDeleteModal = (user: User) => {
    setDeleteModal({
      isOpen: true,
      userId: user.studentId,
      userName: `${user.firstName} ${user.lastName}`,
      userRole: normalizeRole(user.role),
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, userId: null, userName: "", userRole: "" });
  };

  const confirmDelete = () => {
    if (deleteModal.userId) {
      handleDelete(deleteModal.userId, deleteModal.userRole);
    }
  };

  const Chips = ({ items }: { items: string[] }) => {
    if (!items || items.length === 0)
      return <span className="text-gray-400 text-xs">—</span>;
    return (
      <div className="flex flex-nowrap gap-1 overflow-hidden">
        {items.map((txt, i) => (
          <span
            key={txt + i}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs border bg-blue-50 text-blue-800 border-blue-200 whitespace-nowrap"
            title={txt}
          >
            {txt.length > 28 ? txt.slice(0, 28) + "…" : txt}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-fadeIn">
            {/* Modal Header */}
            <div className="bg-red-500 p-6 flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-full">
                <FiAlertTriangle className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Confirm Delete</h3>
                <p className="text-red-100 text-sm">
                  This action cannot be undone
                </p>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete this{" "}
                <span className="font-semibold text-gray-900">
                  {deleteModal.userRole.toLowerCase()}
                </span>
                ?
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600 mb-1">Name:</p>
                <p className="font-semibold text-gray-900">
                  {deleteModal.userName}
                </p>
              </div>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This will remove the{" "}
                  {deleteModal.userRole.toLowerCase()} from all assigned batches
                  and delete all related enrollments.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-end">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <FiTrash2 />
                Delete {deleteModal.userRole}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEW HEADER - Same as EditQuizPage */}
      <PageHeader
        icon={<FaUserShield />}
        title="Admins & Mentors"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Admins & Mentors" },
        ]}
      />

      <div className="flex justify-end gap-2 max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <Link
          to="/admin/register-mentor"
          className="flex items-center gap-2 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors text-sm sm:text-base font-semibold shadow-md"
          style={{ fontSize: "16px" }}
        >
          <FiUserPlus className="w-4 h-4" />
          <span className="hidden sm:inline">Add Mentor</span>
          <span className="sm:hidden">Add</span>
        </Link>

        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base backdrop-blur-sm"
          style={{ fontSize: "16px" }}
        >
          <FaSync
            className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
          />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Body */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {loading ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
            <p className="text-gray-600">Loading data...</p>
          </div>
        ) : err ? (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
              <p className="font-semibold">Error</p>
              <p>{err}</p>
            </div>
            <button
              onClick={fetchData}
              className="w-full bg-[#009fe3] hover:bg-[#007cb5] text-white py-3 rounded-lg font-semibold transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-[1000px] w-full">
                <thead className="bg-[#009fe3] text-white">
                  <tr className="text-left text-xs uppercase tracking-wide">
                    <th className="px-4 py-3 font-semibold">ID</th>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Email</th>
                    <th className="px-4 py-3 font-semibold">WhatsApp</th>
                    <th className="px-4 py-3 font-semibold">Role</th>
                    <th className="px-4 py-3 font-semibold">
                      Assigned Batches
                    </th>
                    <th className="px-4 py-3 font-semibold text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {adminsMentors.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-6 py-10 text-center text-gray-500"
                      >
                        No admins or mentors found.
                      </td>
                    </tr>
                  ) : (
                    adminsMentors.map((u, idx) => {
                      const role = normalizeRole(u.role);
                      const batchesForMentor =
                        role === "MENTOR"
                          ? batchNamesByMentorId[u.studentId] || []
                          : [];
                      return (
                        <tr
                          key={u.studentId}
                          className={
                            idx % 2 === 0 ? "bg-white" : "bg-gray-50/60"
                          }
                        >
                          <td className="px-4 py-3 text-gray-700">
                            {u.studentId}
                          </td>
                          <td className="px-4 py-3 text-gray-800 font-medium">
                            {u.firstName} {u.lastName}
                          </td>
                          <td className="px-4 py-3 text-gray-700">{u.email}</td>
                          <td className="px-4 py-3 text-gray-700">
                            {u.whatsappNumber || "—"}
                          </td>
                          <td className="px-4 py-3 text-gray-700">{role}</td>
                          <td className="px-4 py-3">
                            {role === "MENTOR" ? (
                              <Chips items={batchesForMentor} />
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-nowrap gap-2 justify-end whitespace-nowrap">
                              <Link
                                to={`/admin/edit-student/${u.studentId}?from=mentors`}
                                className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-[#009fe3] text-white hover:bg-[#007cb5] transition-colors"
                              >
                                <FiEdit2 /> Edit
                              </Link>
                              <button
                                onClick={() => openDeleteModal(u)}
                                className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-red-500 text-white hover:bg-red-600 transition-colors"
                              >
                                <FiTrash2 /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ManageAdminsMentors;
